import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { DmcPackageService } from 'src/app/business-service/dmc-package/dmc-package.service';
import { GlobalService } from 'src/app/modules/shared/services/global/global.service';
import { LovDataService } from 'src/app/modules/shared/services/lov-data/lov-data.service';
import { ResponseNotificationService } from 'src/app/modules/shared/services/response-notification/response-notification.service';
import { InputValidationsService } from 'src/app/modules/shared/services/validations/input-validations.service';

@Component({
  selector: 'app-dmc-package-transfers',
  templateUrl: './dmc-package-transfers.component.html',
  styleUrls: ['./dmc-package-transfers.component.scss'],
})
export class DmcPackageTransfersComponent implements OnInit {

  @Input() packageId: any;
  @Input() storedFormData: any;
  @Input() packageDetailsFormData: any;

  @Input() editPackageId: any;

  @Output() viewChange: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() storeFormData: EventEmitter<any> = new EventEmitter<any>();

  formEnabled: boolean = true;

  transfersForm!: FormGroup;

  constructor(
    private _globalService: GlobalService,
    private _fb: FormBuilder,
    private _responseNotificationService: ResponseNotificationService,
    private _dmcPackageService: DmcPackageService,
    private _validationService: InputValidationsService,
    private _lovDataSerivce: LovDataService
  ) { }

  ngOnInit() {
    this.transfersForm = this._fb.group({
      packageId: [this.packageId],
      id: [null],
      organizationId: [null],
      createDate: [null],
      createdBy: [null],
      ownership: [''],
      transfersDetails: this._fb.array([this.createTransfersFormGroup()])
    });

    this.transfersForm.valueChanges.subscribe(() => {
      this.transfersForm.markAsTouched();
      this.transfersForm.markAsDirty();
    })

    if (this.storedFormData) {
      this.patchTransfersPackage(this.storedFormData);
    }
  }

  createTransfersFormGroup(): FormGroup {
    return this._fb.group({
      index: [0],
      vehicleType: ['', [Validators.required]],
      imagePath: [''],
      imageDoc: [null],
      note: [''],
      paxRange: this._fb.group({
        minRange: [null, [Validators.required, this._validationService.valueNotZeroValidator()]],
        maxRange: [null, [Validators.required, this._validationService.valueNotZeroValidator()]]
      }, { validators: this._validationService.minMaxRangeValidator() })
    })
  }

  getTransfersFormArray(): FormArray {
    return this.transfersForm.get('transfersDetails') as FormArray;
  }

  addTransfers() {
    this.getTransfersFormArray().push(this.createTransfersFormGroup());
  }

  removeTransfers(transferIndex: number) {
    this.getTransfersFormArray().removeAt(transferIndex);
  }

  removeTransfersImage(transferIndex: number) {
    this.getTransfersFormArray().at(transferIndex).get('imagePath')?.setValue(null);
    this.getTransfersFormArray().at(transferIndex).get('imageDoc')?.setValue(null);
    this.transfersForm.markAsDirty();
  }

  patchTransfersPackage(formData: any) {
    this.formEnabled = formData.enableTransfer;
    const { transfersDetails, ...transfersPackage } = formData;
    this.transfersForm.patchValue(transfersPackage);

    this.patchTransfersDetailsArray(transfersDetails);

    this.transfersForm.markAsPristine();
  }

  patchTransfersDetailsArray(transfersDetails: any) {
    this.getTransfersFormArray().clear();
    transfersDetails.forEach((transfers: any) => {
      const formGroup = this.createTransfersFormGroup();
      formGroup.patchValue({
        ...transfers,
        imagePath: transfers.imagePath,
        imageDoc: transfers.imagePath
      });
      this.getTransfersFormArray().push(formGroup);
    });
  }

  // image upload funtions
  transferImageSelected(event: any, transferIndex: number) {
    const file = event.target.files[0];
    // Checking if image is more than 5MB
    if (file.size > 5 * 1024 * 1024) {
      this._globalService.customAlert("File size should be less than 5MB", "Invalid File Size");
      return;
    }
    let transferControlGroup = this.getTransfersFormArray().at(transferIndex);
    if (file) {
      const formData = new FormData();
      formData.append('image', file);
      this._lovDataSerivce.uploadImage(formData, "dmcOffers").subscribe({
        next: (res) => {
          transferControlGroup.get('imagePath')?.setValue(res);
          transferControlGroup.get('imageDoc')?.setValue(file);
          this.transfersForm.markAsDirty();
        },
        error: (error) => {
          console.error('Error uploading image: ', error);
          this._responseNotificationService.showMessage("Error uploading image", true);
        }
      })

    }
  }

  getImgUrl(file: any) {
    if (file instanceof File) {
      const fileURL = URL.createObjectURL(file);
      return fileURL;
    } else {
      return file;
    }
  }

  // Following funtoins is called from parent component (hotel pricing component)
  async onSubmit(): Promise<boolean> {
    let formData = this.serializeFormData(this.transfersForm.value);

    return new Promise((resolve) => {
      this._dmcPackageService.addTransfers(formData).subscribe({
        next: (data: any) => {
          this.storeFormData.emit(data);
        },
        error: (error) => {
          console.error('Error saving Pricing: ', error);
          resolve(false);
        },
        complete: () => {
          this.transfersForm.markAsPristine();
          resolve(true);
        }
      });
    });
  }

  serializeFormData(formData: any): any {
    const serializedData = {
      ...formData,
      enableTransfer: this.formEnabled
    };
    return serializedData;
  }

  async resetForm() {
    if (await this._globalService.confirmActionPopup("Confirm to reset data", "Confirm", "Confirm")) {
      this.ngOnInit();
    }
  }

  isFormValid(): boolean {
    if(this.formEnabled && this.transfersForm.invalid){
      return false;
    }else{
      return true;
    }
  }

  formToggleChanged() {
    this.transfersForm.markAsDirty();
    this.transfersForm.markAsTouched();
  }

  checkInvalidControl(group: any, formControlName: string): boolean {
    const control = group.get(formControlName)
    return control?.invalid && (control?.touched || control?.dirty);
  }
}
